// WO-33 — every onboarding text lives here, not in the components, so an
// Arabic version can be added later by a person (no machine translation).
// The English below is SAMUEL's final copy (Onboarding Spec v1, 21/09/2026),
// with four adaptations MATT made because the app has no "saved search"
// feature: tasks A and B, the two Alerts empty screens and tour step 2.
// Each one is marked ADAPTED.
//
// `ar` is deliberately empty. While it is empty, onboarding pieces with no
// Arabic text show in English (left-to-right) for an Arabic reader, and the
// empty screens keep the page's existing Arabic text.

export const PRICING_URL = '/dashboard/settings?tab=plans';
// WO-36 — the help menu's "Contact us" goes to support, where the support
// agent reads it (3 runs a day + on demand). Legal notices keep info@.
export const CONTACT_EMAIL = 'support@mid2bid.com';

export const onboardingCopy = {
  en: {
    welcome: {
      title: 'Welcome to Mid2Bid',
      intro:
        'Tenders, projects and market news from the six Gulf markets, with a CRM to follow them up. What brings you here?',
      options: {
        tenders: { title: 'Finding tenders to bid on', line: 'Public tenders, filtered to what you do.' },
        projects_news: { title: 'Following projects and market news', line: 'Who is building what, and the news behind it.' },
        visibility: { title: 'Getting my company found', line: 'Put your company and projects where buyers search.' },
      },
      getStarted: 'Get started',
      showMeAround: 'Show me around',
      justLooking: 'Just looking around',
      close: 'Close',
    },
    checklist: {
      title: 'Getting started',
      progress: (done, total) => `${done} of ${total} done`,
      pill: (done, total) => `Getting started · ${done} of ${total}`,
      go: 'Go',
      seePlans: 'See plans',
      hide: 'Hide checklist',
      minimise: 'Minimise',
      open: 'Open checklist',
      allSet: "You're set up. Your checklist is always in the ? menu.",
      tasks: {
        // ADAPTED by MATT: the app has no "saved search"; the heart
        // (favourites) is what every plan can save.
        A: { title: 'Save a tender', line: 'Tap the heart on any tender to keep it in your favourites and find it again in one click.' },
        // ADAPTED by MATT: an alert is created from the Tenders filters.
        B: { title: 'Get alerts on new tenders', line: "Filter the tenders list, then use Create alert. We'll email you when a new tender matches." },
        C: { title: 'Add an opportunity to your CRM', line: 'Found something worth chasing? Use Add to CRM on any tender, project or news item. It gets a stage, an owner and a next step.' },
        D: {
          title: 'Get your company found',
          line: 'Add your company profile or a project. It joins the same pages other members search.',
          linePremium: 'Add your company, a project, a news item or a tender. It joins the same pages other members search.',
          lineLocked: 'Publish your company and projects where buyers search.',
        },
        E: {
          title: 'Browse market reports',
          line: 'Market reports on the six Gulf markets, available on every plan.',
          linePaid: 'Your plan includes free reports every month. Pick one.',
        },
        team: { title: 'Invite your team', line: 'Share one pipeline, so an opportunity has an owner instead of an email thread.' },
      },
      planBadge: { standard: 'Standard', premium: 'Premium' },
    },
    empty: {
      crm: { title: 'No opportunities yet', text: 'Open any tender, project or news item and use Add to CRM. It lands here with a stage, an owner and a next step.', button: 'Browse tenders' },
      contacts: { title: 'No contacts yet', text: 'Contacts appear here as you add them to your opportunities.', button: 'Open my CRM' },
      // ADAPTED by MATT: "My saved searches" does not exist; the button opens
      // the page's own Create alert dialog.
      alerts: { title: 'No alerts yet', text: "Filter the tenders list, or create an alert here. We'll email you when a new tender matches.", button: 'Create an alert' },
      // ADAPTED by MATT: "save searches" → "save tenders to your favourites".
      alertsFree: { title: 'Alerts come with Standard', text: 'You can save tenders to your favourites on the Free plan. To get an email when a new tender matches your filters, move to Standard.', button: 'See plans' },
      myReports: { title: 'No reports yet', text: 'Reports you buy or redeem appear here, ready to download.', button: 'Browse reports' },
      team: { title: "It's just you so far", text: 'Invite colleagues to share one pipeline. Every opportunity gets an owner, and nothing gets lost in forwarded emails.', button: 'Invite a colleague' },
      noResults: { title: 'Nothing matches these filters', text: 'Try removing a filter or widening the dates.', button: 'Clear filters' },
    },
    tour: {
      steps: [
        'Everything lives here: tenders, projects, news, companies, reports and your CRM.',
        // ADAPTED by MATT: the second sentence promised saved filters, which do not exist.
        'Narrow any list by market, sector and status.',
        'Worth chasing? Add to CRM turns it into an opportunity with a stage, an owner and a next step.',
        'Your checklist, this tour and our contact details are always here.',
      ],
      back: 'Back',
      next: 'Next',
      done: 'Done',
      close: 'Close tour',
      counter: (i, n) => `${i} of ${n}`,
    },
    help: {
      button: 'Help',
      gettingStarted: 'Getting started',
      showMeAround: 'Show me around',
      demo: 'Watch the demo',
      contact: 'Contact us',
    },
    addToCrm: { label: 'Add to CRM', added: 'Added to your CRM', already: 'This tender is already in your CRM', inCrm: 'In your CRM', failed: 'Could not add this tender to your CRM' },
  },
  ar: {},
};

// English unless a person has written the Arabic (spec §7).
export function copyFor(language) {
  const ar = onboardingCopy.ar;
  if (language === 'ar' && ar && Object.keys(ar).length > 0) return ar;
  return onboardingCopy.en;
}

export function hasArabicCopy() {
  return Object.keys(onboardingCopy.ar || {}).length > 0;
}

export function pricingUrl(src) {
  return `${PRICING_URL}&src=${encodeURIComponent(src)}`;
}
